import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/ui/Button';
import ProgressIndicator from '../profile-completion-wizard-step-1-role-confirmation/components/ProgressIndicator';
import ProfileSummary from './components/ProfileSummary';
import VerificationStatus from './components/VerificationStatus';
import WelcomeBenefits from './components/WelcomeBenefits';
import profileService from '../../services/profileService';

const ProfileCompletionWizardStep3 = () => {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const [profileData, setProfileData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCompleting, setIsCompleting] = useState(false);

  useEffect(() => {
    // Redirect if profile already completed
    if (userProfile?.profile_completed) {
      const roleRoutes = {
        superadmin: '/super-admin-dashboard',
        client: '/client-job-management',
        contractor: '/contractor-dashboard',
        affiliate: '/affiliate-dashboard'
      };
      navigate(roleRoutes?.[userProfile?.role] || '/contractor-dashboard');
      return;
    }

    loadProfileData();
  }, [userProfile, navigate]);

  const loadProfileData = async () => {
    if (!user?.id || !userProfile?.role) return;

    setIsLoading(true);
    try {
      // Load basic profile
      const basicProfile = await profileService?.getUserProfile(user?.id);
      
      let roleSpecificData = {};
      
      // Load role-specific data
      if (userProfile?.role === 'contractor') {
        const contractorProfile = await profileService?.getContractorProfile(user?.id);
        if (contractorProfile) {
          roleSpecificData = {
            skills: contractorProfile?.skills || [],
            experienceLevel: contractorProfile?.experience_level,
            certifications: contractorProfile?.certifications || [],
            hourlyRate: contractorProfile?.hourly_rate,
            availability: contractorProfile?.availability || [],
            travelDistance: contractorProfile?.travel_distance,
            serviceTypes: contractorProfile?.service_types || []
          };
        }
      } else if (userProfile?.role === 'client') {
        const clientProfile = await profileService?.getClientProfile(user?.id);
        if (clientProfile) {
          roleSpecificData = {
            businessType: clientProfile?.business_type,
            businessRegistration: clientProfile?.business_registration,
            billingAddress: clientProfile?.billing_address,
            servicePreferences: clientProfile?.service_preferences || []
          };
        }
      } else if (userProfile?.role === 'affiliate') {
        const affiliateProfile = await profileService?.getAffiliateProfile(user?.id);
        if (affiliateProfile) {
          roleSpecificData = {
            companyName: affiliateProfile?.company_name,
            networkType: affiliateProfile?.network_type,
            referralCapabilities: affiliateProfile?.referral_capabilities || [],
            preferredCommissionStructure: affiliateProfile?.preferred_commission_structure,
            commissionRate: affiliateProfile?.commission_rate
          };
        }
      }

      setProfileData({
        ...basicProfile,
        ...roleSpecificData
      });
    } catch (error) {
      console.error('Error loading profile:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleComplete = async () => {
    setIsCompleting(true);
    try {
      // Mark profile as completed
      await profileService?.updateProfileCompletion(user?.id, 3, true);
      
      // Navigate to appropriate dashboard
      const roleRoutes = {
        superadmin: '/super-admin-dashboard',
        client: '/client-job-management',
        contractor: '/contractor-dashboard',
        affiliate: '/affiliate-dashboard'
      };
      
      navigate(roleRoutes?.[userProfile?.role] || '/contractor-dashboard');
    } catch (error) {
      console.error('Error completing profile:', error);
      alert('Er is een fout opgetreden bij het voltooien van uw profiel. Probeer het opnieuw.');
    } finally {
      setIsCompleting(false);
    }
  };

  const handleEdit = () => {
    navigate('/profile-completion-wizard-step-2-role-specific-information');
  };

  const handleBack = () => {
    navigate('/profile-completion-wizard-step-2-role-specific-information');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Laden...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8 px-4">
      <div className="max-w-4xl mx-auto space-y-8">
        {/* Header */}
        <div className="text-center space-y-2">
          <h1 className="text-3xl md:text-4xl font-bold text-foreground">
            Bijna klaar! 🎉
          </h1>
          <p className="text-lg text-muted-foreground">
            Controleer uw gegevens en voltooi uw profiel
          </p>
        </div>

        {/* Progress Indicator */}
        <ProgressIndicator
          currentStep={3}
          totalSteps={3}
          estimatedTime="2 minuten"
        />

        {/* Congratulations Message */}
        <div className="bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20 rounded-lg p-6 text-center space-y-2">
          <div className="text-4xl mb-2">✨</div>
          <h2 className="text-2xl font-semibold text-foreground">
            Gefeliciteerd!
          </h2>
          <p className="text-muted-foreground">
            U heeft alle benodigde informatie ingevuld. Controleer hieronder uw gegevens en voltooi uw profiel om toegang te krijgen tot het platform.
          </p>
        </div>

        {/* Profile Summary */}
        <ProfileSummary
          profileData={profileData}
          role={userProfile?.role}
          onEdit={handleEdit}
        />

        {/* Verification Status */}
        <VerificationStatus role={userProfile?.role} />

        {/* Welcome Benefits */}
        <WelcomeBenefits role={userProfile?.role} />

        {/* Trust Indicators */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div 
            className="text-center p-4 bg-white border-2 border-slate-200 rounded-lg"
            style={{
              boxShadow: '0 15px 40px rgba(0,0,0,0.15), 0 8px 20px rgba(0,0,0,0.1)'
            }}
          >
            <p className="text-2xl font-bold text-primary">1000+</p>
            <p className="text-sm text-muted-foreground">Actieve gebruikers</p>
          </div>
          <div 
            className="text-center p-4 bg-white border-2 border-slate-200 rounded-lg"
            style={{
              boxShadow: '0 15px 40px rgba(0,0,0,0.15), 0 8px 20px rgba(0,0,0,0.1)'
            }}
          >
            <p className="text-2xl font-bold text-primary">500+</p>
            <p className="text-sm text-muted-foreground">Voltooide opdrachten</p>
          </div>
          <div 
            className="text-center p-4 bg-white border-2 border-slate-200 rounded-lg"
            style={{
              boxShadow: '0 15px 40px rgba(0,0,0,0.15), 0 8px 20px rgba(0,0,0,0.1)'
            }}
          >
            <p className="text-2xl font-bold text-primary">4.8/5</p>
            <p className="text-sm text-muted-foreground">Gemiddelde beoordeling</p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button
            variant="outline"
            size="lg"
            onClick={handleBack}
            iconName="ArrowLeft"
            iconPosition="left"
          >
            Terug
          </Button>
          <Button
            variant="default"
            size="lg"
            onClick={handleComplete}
            loading={isCompleting}
            iconName="Check"
            iconPosition="right"
          >
            Profiel Voltooien
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProfileCompletionWizardStep3;